// src/apiClient.js

import axios from 'axios';
import { apiRequest } from './msalConfig';
import { getActiveAccount, acquireTokenSilently } from './services/msalInstanceManager';
import { classifyError, logError } from './utils/errorHandler';

const apiUrl = process.env.REACT_APP_API_URL;

// Request deduplication: Track pending requests to prevent duplicates
const pendingRequests = new Map();

/**
 * Generate a unique key for a request based on method, url, and params
 * @param {object} config - Axios request config
 * @returns {string} Unique request key
 */
const getRequestKey = (config) => {
  const { method = 'get', url, params, data } = config;
  return `${method.toUpperCase()}:${url}:${JSON.stringify(params || {})}:${JSON.stringify(data || {})}`;
};

/**
 * Create an axios instance with request deduplication
 * @param {string} accessToken - Optional access token for authenticated requests
 * @returns {AxiosInstance} Configured axios instance
 */
const createAxiosInstance = (accessToken) => {
  const instance = axios.create({
    baseURL: apiUrl,
    headers: accessToken ? {
      Authorization: `Bearer ${accessToken}`,
    } : {},
  });

  // Request interceptor: Deduplicate concurrent identical requests
  instance.interceptors.request.use(
    (config) => {
      // Only deduplicate GET requests by default (safe and idempotent)
      // Can be extended to other methods if needed
      if (config.method?.toLowerCase() === 'get') {
        const requestKey = getRequestKey(config);
        
        // Check if an identical request is already pending
        if (pendingRequests.has(requestKey)) {
          console.log(`[API] Deduplicating request: ${requestKey}`);
          // Return the existing promise instead of making a new request
          config.adapter = () => pendingRequests.get(requestKey);
        }
      }
      
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor: Clean up completed requests from pending map and handle errors
  instance.interceptors.response.use(
    (response) => {
      // Remove from pending requests on success
      if (response.config.method?.toLowerCase() === 'get') {
        const requestKey = getRequestKey(response.config);
        pendingRequests.delete(requestKey);
      }
      return response;
    },
    (error) => {
      // Remove from pending requests on error
      if (error.config?.method?.toLowerCase() === 'get') {
        const requestKey = getRequestKey(error.config);
        pendingRequests.delete(requestKey);
      }
      
      // Enhanced error handling with classification
      const errorType = classifyError(error);
      const context = {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        errorType
      };
      
      // Log error with context (will use Application Insights in production)
      logError(error, context);
      
      // Enhance error object with classification for better handling in components
      error.errorType = errorType;
      error.isRetryable = ['NETWORK', 'TIMEOUT', 'SERVER', 'RATE_LIMIT'].includes(errorType);
      
      return Promise.reject(error);
    }
  );

  // Wrap axios methods to track pending requests
  const originalRequest = instance.request.bind(instance);
  instance.request = function (config) {
    if (config.method?.toLowerCase() === 'get') {
      const requestKey = getRequestKey(config);
      
      if (!pendingRequests.has(requestKey)) {
        const requestPromise = originalRequest(config);
        pendingRequests.set(requestKey, requestPromise);
        
        // Clean up after the request completes (success or failure)
        requestPromise
          .finally(() => {
            pendingRequests.delete(requestKey);
          });
      }
    }
    
    return originalRequest(config);
  };

  return instance;
};

const apiClient = async (instance) => {
    if (instance) {
        try {
            // Use centralized active account management
            const activeAccount = getActiveAccount(instance);
            
            if (!activeAccount) {
                console.warn("No active account available for API call");
                return createAxiosInstance();
            }

            // Use centralized token acquisition
            const tokenResponse = await acquireTokenSilently(instance, apiRequest);

            const accessToken = tokenResponse.accessToken;

            if (!accessToken) {
                throw new Error("Access Token is undefined or empty");
            }

            return createAxiosInstance(accessToken);
        } catch (error) {
            console.error("Failed to acquire token:", error);
            
            // If it's an interaction required error, let the caller handle it
            if (error.name === 'InteractionRequiredAuthError') {
                console.log("Interactive login required");
            }
            
            throw error;
        }
    }
    return createAxiosInstance();
};

export default apiClient;